/**
 * Pure helpers for describing a resource item's file.
 *
 * Kept free of side effects — the actual download goes through
 * `downloadBlob` in `lib/utils/download.utils.ts`.
 */

/** How a file can be shown inside the detail dialog. */
export type FilePreviewKind = 'pdf' | 'image' | 'none';

/**
 * Preview kind per MIME type. Only the types the backend serves with an
 * `inline` Content-Disposition can render in place; everything else
 * (DOCX, XLSX) is download-only, matching the server's `attachment` header.
 */
const PREVIEW_KIND_BY_MIME: Record<string, FilePreviewKind> = {
  'application/pdf': 'pdf',
  'image/png': 'image',
  'image/jpeg': 'image',
};

/** Extension fallback when the stored key has none. */
const EXTENSION_BY_MIME: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
};

/** Characters a browser or OS will not accept in a saved filename. */
const UNSAFE_FILENAME_CHARS = /[\\/:*?"<>|]+/g;

/**
 * Decide how a file should be previewed.
 *
 * @param mimeType - the item's stored MIME type, or null when it has no file
 * @returns the preview kind; `none` for download-only types
 */
export function resolveFilePreviewKind(mimeType: string | null | undefined): FilePreviewKind {
  if (!mimeType) return 'none';
  return PREVIEW_KIND_BY_MIME[mimeType] ?? 'none';
}

/**
 * Build the filename to save a resource file under.
 *
 * The stored S3 key is `resources/files/<userId>/<uuid>.<ext>` — a UUID is
 * useless to whoever opens their downloads folder, so the file is named after
 * the resource and only its extension is taken from the key (falling back to
 * the MIME type when the key has none).
 *
 * @param title - the resource title
 * @param fileUrl - the stored S3 key, or null
 * @param mimeType - the stored MIME type, used only for the extension fallback
 * @returns a filename safe to pass to an anchor's `download` attribute
 */
export function resourceFileName(
  title: string,
  fileUrl: string | null | undefined,
  mimeType: string | null | undefined,
): string {
  // Sanitising can leave a name made only of the replacement character (a
  // title of "///" becomes "-"), which is truthy but useless — so strip
  // leading/trailing separators before deciding whether anything survived.
  const base =
    title.replace(UNSAFE_FILENAME_CHARS, '-').replace(/^[-\s]+|[-\s]+$/g, '') || 'resource';

  const keyExtension = fileUrl?.split('.').pop();
  const extension =
    keyExtension && keyExtension !== fileUrl
      ? keyExtension
      : mimeType
        ? (EXTENSION_BY_MIME[mimeType] ?? '')
        : '';

  return extension ? `${base}.${extension}` : base;
}
